<script lang="ts">
  import { Sparkles, ShieldCheck, Coffee, Plane, Calendar, BarChart3, Lock, Heart } from "lucide-svelte";
  import Sheet from "./ui/Sheet.svelte";
  import { BUCKETS } from "./bucketize";

  export let open = false;
  export let onClose: () => void;

  const ICONS: Record<string, any> = {
    weekly: Coffee,
    travel: Plane,
    monthly: Calendar,
    yearly: BarChart3
  };
</script>

<Sheet {open} title="怎么工作的？" {onClose}>
  <section class="space-y-3">
    <div class="flex items-center gap-2">
      <Sparkles class="size-4 text-primary" />
      <h3 class="font-label text-foreground">默认卡 + 例外</h3>
    </div>
    <p class="text-sm leading-relaxed text-muted-foreground">
      我们参考 Plasma / Pulse 的"一张卡，一条规则"思路：在你持有的卡里挑一张
      <span class="font-medium text-foreground">综合得分最高的卡</span>当默认。
      其他卡只在它们对某个场景明显更优时，作为"例外"加入清单。
    </p>
    <p class="text-sm leading-relaxed text-muted-foreground">
      算法只用<span class="font-medium text-foreground">高置信权益</span>参与打分——
      规则不明确、来源未核验的暂时不会进推荐。你可以手动「换张卡当默认」覆盖算法。
    </p>
  </section>

  <section class="mt-6 space-y-3">
    <div class="flex items-center gap-2">
      <ShieldCheck class="size-4 text-success" />
      <h3 class="font-label text-foreground">置信度分层</h3>
    </div>
    <ul class="space-y-2 text-sm leading-relaxed text-muted-foreground">
      <li>
        <span class="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">高置信</span>
        — 进默认卡推荐 / 主榜 / 例外清单。
      </li>
      <li>
        <span class="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold">中置信</span>
        — 进 bucket 主榜（带角标），不进默认卡评分。
      </li>
      <li>
        <span class="rounded-full bg-warn/15 px-2 py-0.5 text-[10px] font-semibold text-warn">待核实</span>
        — 单独折叠在每个 bucket 内，仅供参考。
      </li>
    </ul>
  </section>

  <section class="mt-6 space-y-3">
    <div class="flex items-center gap-2">
      <Coffee class="size-4 text-primary" />
      <h3 class="font-label text-foreground">5 个 bucket</h3>
    </div>
    <p class="text-sm leading-relaxed text-muted-foreground">
      按"<span class="font-medium text-foreground">你什么时候要想它</span>"分类，不是按权益类型分类。
      用 5 个固定容器养成习惯：
    </p>
    <ul class="space-y-2">
      {#each BUCKETS as b}
        {@const Ico = ICONS[b.id]}
        <li class="flex items-start gap-3 rounded-xl bg-secondary/40 px-3 py-2.5">
          <span class="text-lg">{b.emoji}</span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-foreground">{b.title} · <span class="text-[11px] font-normal text-muted-foreground">{b.cadence}</span></p>
            <p class="mt-0.5 text-xs text-muted-foreground">{b.description}</p>
          </div>
        </li>
      {/each}
    </ul>
  </section>

  <section class="mt-6 space-y-3">
    <div class="flex items-center gap-2">
      <Lock class="size-4 text-primary" />
      <h3 class="font-label text-foreground">你的数据</h3>
    </div>
    <p class="text-sm leading-relaxed text-muted-foreground">
      画像 / 卡包 / 反馈 / 自录卡全部存在<span class="font-medium text-foreground"> 浏览器 localStorage </span>里，
      不上传服务器。清浏览器数据 = 清账户。
    </p>
    <p class="text-sm leading-relaxed text-muted-foreground">
      自录卡的"我有这张"信号会用于调整收录优先级，但只包含卡名 / 发卡行 / 你选填的邮箱。
    </p>
  </section>

  <section class="mt-6 space-y-3">
    <div class="flex items-center gap-2">
      <Heart class="size-4 text-rose-500" />
      <h3 class="font-label text-foreground">为什么做这个</h3>
    </div>
    <p class="text-sm leading-relaxed text-muted-foreground">
      多卡持有者每天都要做"该用哪张"的决策。我们想把这个决策变成
      <span class="font-medium text-foreground">「一张默认卡 + ≤5 条能背下来的例外」</span>，
      然后让你忘掉这个 App。
    </p>
    <p class="text-sm leading-relaxed text-muted-foreground">
      你刷卡前不用打开 = 我们成功了。
    </p>
  </section>
</Sheet>
